import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { RADIAL_BAR_FILLS } from 'src/features/home/types';
import PieChart from 'src/features/statistic/components/chart/PieChart';
import { PaymentByMethodStatistic } from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { CURRENCY } from 'src/types';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { ICON_MAP } from 'src/utils/constants';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { paymentTypeBadgeOptions } from 'src/features/payment/utils/constants';

type Props = {
    currency: CURRENCY;
    isLoading: boolean;
    data?: PaymentByMethodStatistic[];
};
export default function WidgetPaymentByMethod({ isLoading, currency, data }: Props) {
    const { formatMessage, formatNumber } = useIntl();

    const columns: ColumnDef<PaymentByMethodStatistic>[] = [
        {
            accessorKey: 'method_name',
            header: formatMessage(statisticMessages.method),
            cell: ({ row }) => formatMessage(paymentTypeBadgeOptions[row.original.method_name].label),
        },
        {
            accessorKey: 'total_amount',
            header: formatMessage(statisticMessages.total),
            cell: ({ row }) => {
                return formatNumber(row.original.total_amount / 100, {
                    style: 'currency',
                    currency,
                });
            },
        },
        {
            accessorKey: 'transactions_count',
            header: formatMessage(statisticMessages.transactions),
        },
        {
            accessorKey: 'average_amount',
            header: formatMessage(statisticMessages.average),
            cell: ({ row }) => {
                return formatNumber(Number(row.original.average_amount) / 100, {
                    style: 'currency',
                    currency,
                });
            },
        },
    ];

    return (
        <GeneralBlock className="min-h-110 flex-1">
            <SectionBlockTitle>{formatMessage(statisticMessages.paymentsByMethod)}</SectionBlockTitle>
            {isLoading ? (
                <Skeleton className="h-107" />
            ) : data && data.length > 0 ? (
                <>
                    <div className="flex h-full w-full justify-center gap-2 px-10">
                        <PieChart data={data} dataKey="total_amount" />
                        <div className="flex flex-col gap-1">
                            {data?.map((entry, i) => {
                                return (
                                    <div key={i} className="flex items-center gap-2">
                                        <div
                                            style={{
                                                backgroundColor: RADIAL_BAR_FILLS[i % RADIAL_BAR_FILLS.length],
                                                width: 10,
                                                height: 10,
                                                borderRadius: 50,
                                            }}
                                        />
                                        <span>{formatMessage(paymentTypeBadgeOptions[entry.method_name].label)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <StatisticTable
                        title={formatMessage(statisticMessages.breakdown)}
                        data={data}
                        columns={columns as any}
                    />
                </>
            ) : (
                <GeneralMessageBlock
                    icon={ICON_MAP.SEARCH}
                    title={formatMessage(statisticMessages.noPaymentsFound)}
                    subtitle={formatMessage(statisticMessages.chooseDifferentPeriod)}
                />
            )}
        </GeneralBlock>
    );
}
